/**
 * TemplateListComponent — a presentational, config-driven list used by every
 * form-builder "templates" surface (registration forms, assessment templates).
 *
 * It renders rows into a column layout with a unified status badge, loading
 * state, empty state, and an actions menu — but owns no feature behaviour:
 * handlers, dialogs, permission checks and data fetching all stay in the
 * feature component, which passes rows/columns/actions in and reacts to the
 * emitted `(action)` / `(rowClick)` outputs.
 *
 * Custom cells (e.g. a linked name + description) are supplied via the
 * `cellTemplate` escape hatch on a column; the parent defines an ng-template
 * (with a `let-row` context) and references it in its column config.
 */
import { NgTemplateOutlet } from '@angular/common';
import { Component, TemplateRef, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltip } from '@angular/material/tooltip';
import {
  TemplateStatus,
  templateStatusBadge,
} from './template-status';

/** How a column's cell is rendered. */
export type TemplateListColumnType =
  | 'text'
  | 'number'
  | 'chips'
  | 'date'
  | 'status-badge'
  | 'custom';

/** A single column in the list. */
export type TemplateListColumn = {
  /** Key read from `row.data` (ignored for `status-badge`, which uses row.status). */
  key: string;
  header: string;
  /** Defaults to `'text'`. */
  type?: TemplateListColumnType;
  align?: 'left' | 'right';
  /** Extra classes for the header cell. */
  headerClass?: string;
  /** Extra classes for the body cell. */
  cellClass?: string;

  /**
   * Escape hatch for `type: 'custom'` — a template rendered with
   * `{ $implicit: row }`. The feature owns any links/markup inside.
   */
  cellTemplate?: TemplateRef<{ $implicit: TemplateListRow }>;
};

/** A row action, surfaced in the per-row overflow menu. */
export type TemplateListAction = {
  id: string;
  label: string;
  /** Lucide svgIcon name. */
  icon: string;
  /** Whether the action is shown for a given row (default: always). */
  visible?: (row: TemplateListRow) => boolean;
  /** Renders the item in a destructive (red) style. */
  danger?: boolean;
};

/** A feature-mapped row. `data` holds column values; `raw` the source item. */
export type TemplateListRow<T = unknown> = {
  id: string;
  data: Record<string, unknown>;
  /** Normalised status, drives the `status-badge` column. */
  status?: TemplateStatus;
  /** The original feature item, echoed back on action/rowClick emits. */
  raw?: T;
};

/** Payload emitted when a row action is chosen. */
export type TemplateListActionEvent = {
  action: string;
  row: TemplateListRow;
};

@Component({
  selector: 'form-template-list',
  imports: [
    NgTemplateOutlet,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatProgressBar,
    MatTooltip,
  ],
  host: { class: 'block' },
  template: `
    @if (loading()) {
      <mat-progress-bar mode="indeterminate" />
    }

    @if (rows().length) {
      <div
        class="overflow-x-auto rounded-xl border bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
      >
        <table class="w-full text-sm">
          <thead>
            <tr
              class="border-b bg-neutral-50 text-left text-xs font-medium uppercase tracking-wider text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950"
            >
              @for (col of columns(); track col.key) {
                <th
                  class="px-4 py-3"
                  [class.text-right]="col.align === 'right'"
                  [class]="col.headerClass"
                >
                  {{ col.header }}
                </th>
              }
              @if (actions().length) {
                <th class="px-4 py-3 text-right">Actions</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of rows(); track row.id) {
              <tr
                class="border-b transition-colors hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-950/50"
              >
                @for (col of columns(); track col.key) {
                  <td
                    class="px-4 py-3 align-top"
                    [class.text-right]="col.align === 'right'"
                    [class]="col.cellClass"
                  >
                    @switch (col.type) {
                      @case ('custom') {
                        @if (col.cellTemplate) {
                          <ng-container
                            [ngTemplateOutlet]="col.cellTemplate"
                            [ngTemplateOutletContext]="{ $implicit: row }"
                          />
                        }
                      }
                      @case ('status-badge') {
                        <span
                          class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                          [class]="badge(row).chipClass"
                        >
                          {{ badge(row).label }}
                        </span>
                      }
                      @case ('date') {
                        <span class="text-neutral-600 dark:text-neutral-400">
                          {{ dateText(row.data[col.key]) }}
                        </span>
                      }
                      @case ('chips') {
                        <div class="flex flex-wrap gap-1.5">
                          @for (chip of chipList(row.data[col.key]); track chip) {
                            <span
                              class="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                            >
                              {{ chip }}
                            </span>
                          } @empty {
                            <span class="text-neutral-400">—</span>
                          }
                        </div>
                      }
                      @default {
                        <span
                          [class.text-neutral-600]="col.type !== 'number'"
                          [class.dark:text-neutral-400]="col.type !== 'number'"
                          [class.text-neutral-500]="col.type === 'number'"
                        >
                          {{ cellText(row.data[col.key]) }}
                        </span>
                      }
                    }
                  </td>
                }
                @if (actions().length) {
                  <td class="px-4 py-3 text-right align-top">
                    <button
                      matIconButton
                      aria-label="Row actions"
                      matTooltip="Actions"
                      [matMenuTriggerFor]="rowMenu"
                    >
                      <mat-icon svgIcon="ellipsis-vertical" class="size-4" />
                    </button>
                    <mat-menu #rowMenu="matMenu">
                      @for (act of visibleActions(row); track act.id) {
                        <button
                          mat-menu-item
                          [class.text-red-600]="act.danger"
                          (click)="action.emit({ action: act.id, row })"
                        >
                          <mat-icon [svgIcon]="act.icon" class="size-4" />
                          <span>{{ act.label }}</span>
                        </button>
                      }
                    </mat-menu>
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else if (!loading()) {
      <!-- Empty state — feature-specific actions project into [list-empty-actions]. -->
      <div
        class="flex flex-col items-center gap-y-3 rounded-xl border-2 border-dashed border-neutral-300 p-12 dark:border-neutral-700"
      >
        <div
          class="flex size-16 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-950"
        >
          <mat-icon [svgIcon]="emptyIcon()" class="size-8 text-blue-500" />
        </div>
        <h3 class="text-lg font-semibold text-neutral-500">{{ emptyTitle() }}</h3>
        @if (emptyMessage()) {
          <p class="max-w-md text-center text-sm text-neutral-400">
            {{ emptyMessage() }}
          </p>
        }
        <ng-content select="[list-empty-actions]" />
      </div>
    }
  `,
})
export class TemplateListComponent {
  readonly columns = input.required<TemplateListColumn[]>();
  readonly rows = input.required<TemplateListRow[]>();
  readonly actions = input<TemplateListAction[]>([]);
  readonly loading = input(false);
  readonly emptyIcon = input('clipboard-list');
  readonly emptyTitle = input('Nothing here yet');
  readonly emptyMessage = input<string | null>(null);

  readonly action = output<TemplateListActionEvent>();

  protected badge(row: TemplateListRow) {
    return templateStatusBadge(row.status ?? row.data['status']);
  }

  protected visibleActions(row: TemplateListRow): TemplateListAction[] {
    return this.actions().filter((a) => !a.visible || a.visible(row));
  }

  protected chipList(value: unknown): string[] {
    if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
    return value == null || value === '' ? [] : [String(value)];
  }

  protected cellText(value: unknown): string {
    if (value == null || value === '') return '—';
    return String(value);
  }

  protected dateText(value: unknown): string {
    if (value == null || value === '') return '—';
    const date = value instanceof Date ? value : new Date(value as string);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  }
}
